import { ORDERING_CHANNELS } from '~/constants/orderingChannels';
import { TTranslate } from '~/types/main';

export interface IResponseReportBody {
  orders_count?: number;
  revenue?: number;
  average_check?: number;
  pickup_count?: number;
  pickup_revenue?: number;
  delivery_count?: number;
  delivery_revenue?: number;
  dinein_count?: number;
  dinein_revenue?: number;

  // Fields for item sales report
  items_count?: number;
  pickup_items_count?: number;
  delivery_items_count?: number;
  dinein_items_count?: number;

  // Fields for combo sales report
  combo_count?: number;
  combo_price?: number;
  pickup_combo_count?: number;
  delivery_combo_count?: number;
  dinein_combo_count?: number;
}
type TResponseReportBodyWithoutItems = Omit<
  IResponseReportBody,
  'items_count' | 'pickup_items_count' | 'delivery_items_count' | 'dinein_items_count'
>;

/** General */
export type TResponseReport = Record<number, IResponseReportBody>;

export const reportBodyCast = (data: IResponseReportBody) => ({
  ordersCount: Number(data.orders_count || 0) || 0,
  revenue: Number(data.revenue || 0) || 0,
  averageCheck: Number(data.average_check || 0) || 0,
  pickupCount: Number(data.pickup_count || 0) || 0,
  pickupRevenue: Number(data.pickup_revenue || 0) || 0,
  deliveryCount: Number(data.delivery_count || 0) || 0,
  deliveryRevenue: Number(data.delivery_revenue || 0) || 0,
  dineinCount: Number(data.dinein_count || 0) || 0,
  dineinRevenue: Number(data.dinein_revenue || 0) || 0,

  // Fields for item sales report
  itemsCount: Number(data.items_count || 0) || 0,
  pickupItemsCount: Number(data.pickup_items_count || 0) || 0,
  deliveryItemsCount: Number(data.delivery_items_count || 0) || 0,
  dineinItemsCount: Number(data.dinein_items_count || 0) || 0,

  // Fields for combo sales report
  comboCount: Number(data.combo_count || 0) || 0,
  comboPrice: Number(data.combo_price || 0) || 0,
  pickupComboCount: Number(data.pickup_combo_count || 0) || 0,
  deliveryComboCount: Number(data.delivery_combo_count || 0) || 0,
  dineinComboCount: Number(data.dinein_combo_count || 0) || 0,
});
export type TReportBody = ReturnType<typeof reportBodyCast>;

/** Branches */
export interface IResponseBranchesReportBody extends TResponseReportBodyWithoutItems {
  branch: {
    id: number;
    name: TTranslate;
  };
}
export type TResponseBranchesReport = Record<string, IResponseBranchesReportBody>;

export const branchesBodyCast = (data: IResponseBranchesReportBody) => {
  // eslint-disable-next-line unused-imports/no-unused-vars
  const {
    itemsCount,
    pickupItemsCount,
    deliveryItemsCount,
    dineinItemsCount,
    comboCount,
    comboPrice,
    pickupComboCount,
    deliveryComboCount,
    dineinComboCount,
    ...body
  } = reportBodyCast(data);

  return {
    branch: {
      id: Number(data.branch.id || 0) || 0,
      name: { en: data.branch.name.en || '', ar: data.branch.name.ar || '' },
    },
    ...body,
  };
};
export type TBranchesReportBody = ReturnType<typeof branchesBodyCast>;
export type TBranchesReport = Record<string, TBranchesReportBody>;

/** Delivery zones */
export interface IResponseDeliveryZonesReportBody extends TResponseReportBodyWithoutItems {
  area: {
    id: number;
    name: TTranslate;
  };
}
export type TResponseDeliveryZonesReport = Record<string, IResponseDeliveryZonesReportBody>;

export const deliveryZonesBodyCast = (data: IResponseDeliveryZonesReportBody) => {
  // eslint-disable-next-line unused-imports/no-unused-vars
  const {
    itemsCount,
    pickupItemsCount,
    deliveryItemsCount,
    dineinItemsCount,
    comboCount,
    comboPrice,
    pickupComboCount,
    deliveryComboCount,
    dineinComboCount,
    ...body
  } = reportBodyCast(data);

  return {
    area: {
      id: Number(data.area.id || 0) || 0,
      name: { en: data.area.name.en || '', ar: data.area.name.ar || '' },
    },
    ...body,
  };
};
export type TDeliveryZonesReportBody = ReturnType<typeof deliveryZonesBodyCast>;
export type TDeliveryZonesReport = Record<string, TDeliveryZonesReportBody>;

/** OrderingChannels */
export interface IResponseOrderingChannelsReportBody extends TResponseReportBodyWithoutItems {
  channel_id: ORDERING_CHANNELS;
}
export type TResponseOrderingChannelsReport = Record<ORDERING_CHANNELS, IResponseOrderingChannelsReportBody>;

export const orderingChannelsReportBodyCast = (data: IResponseOrderingChannelsReportBody) => {
  // eslint-disable-next-line unused-imports/no-unused-vars
  const {
    itemsCount,
    pickupItemsCount,
    deliveryItemsCount,
    dineinItemsCount,
    comboCount,
    comboPrice,
    pickupComboCount,
    deliveryComboCount,
    dineinComboCount,
    ...body
  } = reportBodyCast(data);

  return {
    channelId: Number(data.channel_id || 0) || 0,
    ...body,
  };
};
export type TOrderingChannelsReportBody = ReturnType<typeof orderingChannelsReportBodyCast>;
export type TOrderingChannelsReport = Record<string, TOrderingChannelsReportBody>;
